import React, { useRef, useState, FC, ReactNode } from 'react'
import { TabListProps, Tab } from './Tabs.types'

import { Promo } from '../../Promo/src/Promo'
import { SimpleContent } from '../../SimpleContent/src/SimpleContent'
import { CardGrid } from '../../CardList/src/CardGrid/CardGrid'

import { TabControl } from './TabControl'
import TabsContainer, { ChildRef } from './TabsContainer'
import { CommitmentContent } from '../../Commitments/src/CommitmentContent'
import { ErrorBoundary } from '../../ErrorBoundary'

export const Tabs: FC<TabListProps> = ({ content, testId, type }) => {
  const [activeTab, setActiveTab] = useState<Tab>(content.tab[0])
  const buttonsRef = useRef<(HTMLAnchorElement | null)[]>([])
  const mobileButtonRefs = useRef<(HTMLAnchorElement | null)[]>([])
  const tabRefs = useRef<ChildRef>(null)
  const isCommitment = type === 'Commitments'
  const handleTabClick = (index: number): void => {
    tabRefs.current?.focusTab(index)
    setActiveTab(content.tab[index])
  }

  const handleButtonKeydown = (event: React.KeyboardEvent<HTMLAnchorElement>, index: number) => {
    if (event.key === 'Tab' && buttonsRef.current[index] && mobileButtonRefs.current[index]) {
      event.preventDefault()
      if (index < content?.tab?.length - 1) {
        const nextIndex = index + 1
        handleTabClick(nextIndex)
      }
    }
  }

  const renderTabContent = (tab: Tab, index: number): ReactNode => {
    if (isCommitment) {
      return (
        <ErrorBoundary>
          <CommitmentContent
            tab={{ ...tab }}
            index={index}
            buttonsRef={buttonsRef}
            mobileButtonRefs={mobileButtonRefs}
            handleButtonKeydown={handleButtonKeydown}
          />
        </ErrorBoundary>
      )
    }
    if (tab.promo)
      return (
        <ErrorBoundary>
          <Promo {...tab.promo} />
        </ErrorBoundary>
      )
    if (tab.cardGrid)
      return (
        <div className='u-marginTop32'>
          <ErrorBoundary>
            <CardGrid {...tab.cardGrid} />
          </ErrorBoundary>
        </div>
      )
    if (tab.simpleContent)
      return (
        <ErrorBoundary>
          <SimpleContent {...tab.simpleContent} />
        </ErrorBoundary>
      )
    return null
  }

  if (!content.tab || content.tab.length < 2 || content.tab.length > 6) {
    console.log('Invalid commitments tab length')
    return null
  }

  return (
    <div className='u-adel-reset' data-testid={testId}>
      <div className={`${isCommitment ? 'Commitments' : ''} TabsComponent u-posRelative`}>
        {content?.headline && (
          <h2
            className={`Type-2 u-sizeConstrained u-flex u-flexJustifyCenter u-textCenter
          ${
            isCommitment ? 'u-paddingVert48' : 'u-paddingVert16'
          } u-textPrimary u-textColorNeutralGrey`}
          >
            {content.headline}
          </h2>
        )}
        <TabsContainer
          ref={tabRefs}
          tabs={content.tab}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          buttonsRef={buttonsRef}
          mobileButtonRefs={mobileButtonRefs}
          isCommitment={isCommitment}
        />
        <div className={`u-marginBottom32`}>
          <div className={`${isCommitment ? 'u-sizeConstrained' : ''}`}>
            {content.tab?.map((tab: any, index: any) => {
              const isActive: boolean = activeTab?.identifier === tab.identifier
              return (
                <div
                  key={`${tab.identifier}-${index}`}
                  id={`${tab.identifier}-${index}`}
                  role='tabpanel'
                  className='Tab-panel'
                  aria-expanded={isActive}
                  hidden={!isActive}
                >
                  <div className={`TabPanelsShadow Tab-content Content u-Desktop`}>
                    {renderTabContent(tab, index)}
                    <TabControl
                      index={index}
                      handleTabClick={handleTabClick}
                      tabLength={content.tab.length}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
